import {
  AlertTriangle,
  GraduationCap,
  Mail,
  Pencil,
  Send,
  Trash2,
  Users,
  User,
} from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";
import WelcomeCard from "../../components/WelcomeCard";
import Modal from "../../components/modal/Modal";
import { fetchAllCourses } from "../../services/adminService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { api1 } from "../../utils/axiosInstance";

const ListCourse = () => {
  const [modal, setModal] = useState({ type: "", data: null });
  const [email, setEmail] = useState({ subject: "", message: "" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = useSelector((state) => state.auth.id);

  const { data: courses, isLoading, error } = useQuery({
    queryKey: ["totalCourses"],
    queryFn: fetchAllCourses,
    enabled: !!userId,
  });

  const getAllUsers = () => {
    if (!Array.isArray(courses)) return [];
    const users = courses.flatMap((c) => c.registeredUsers || []);
    const unique = users.filter(
      (u, i, arr) => i === arr.findIndex((x) => x._id === u._id)
    );
    return unique.map((u) => ({
      ...u.profile,
      userId: u._id,
      email: u.email,
    }));
  };

  const closeModal = () => setModal({ type: "", data: null });
  const resetEmail = () => setEmail({ subject: "", message: "" });

  const handleDeleteCourse = async () => {
    try {
      await api1.delete(`${API_ENDPOINTS.COURSE}/${modal.data._id}`);
      queryClient.invalidateQueries(["totalCourses"]);
      toast.success("Course deleted successfully");
    } catch {
      toast.error("Failed to delete course");
    } finally {
      closeModal();
    }
  };

  const handleSendEmail = async (endpoint, ids) => {
    const { subject, message } = email;
    if (!subject || !message) return toast.error("Please fill in all fields");

    try {
      await api1.post(endpoint, { ids, subject, message });
      toast.success("Email sent successfully");
    } catch {
      toast.error("Failed to send email");
    } finally {
      resetEmail();
      closeModal();
    }
  };

  if (isLoading) return <SecondaryLoader />;
  if (error)
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center bg-gray-50 rounded-lg shadow-inner border">
        <GraduationCap className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700">
          Error Fetching Courses
        </h3>
        <p className="text-sm text-gray-500 mt-2">
          Please try again later.
        </p>
      </div>
    );

  return (
    <>
      {/* Delete Modal */}
      <Modal isOpen={modal.type === "delete"} onClose={closeModal}>
        <div className="flex flex-col items-center gap-4 p-4 text-center">
          <AlertTriangle className="w-12 h-12 text-red-600" />
          <h2 className="text-lg font-semibold text-gray-800">
            Delete this course?
          </h2>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
          <div className="flex gap-4 w-full">
            <button onClick={closeModal} className="w-full border rounded-lg py-2 hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={handleDeleteCourse}
              className="w-full bg-red-600 text-white rounded-lg py-2 hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Email Modal (Mentor & Mentees) */}
      {(modal.type === "mentorEmail" || modal.type === "menteesEmail") && (
        <Modal isOpen onClose={closeModal}>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {modal.type === "mentorEmail"
                ? "Send Email to Instructor"
                : "Send Email to Registered Users"}
            </h2>
            <div className="space-y-4">
              {["Subject", "Message"].map((label) => (
                <div key={label}>
                  <label className="text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  {label === "Message" ? (
                    <textarea
                      rows={6}
                      value={email.message}
                      onChange={(e) =>
                        setEmail({ ...email, message: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2 mt-1 focus:ring-2 focus:ring-[#7E5BEF]"
                      placeholder="Write your message here..."
                    />
                  ) : (
                    <input
                      type="text"
                      value={email.subject}
                      onChange={(e) =>
                        setEmail({ ...email, subject: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2 mt-1 focus:ring-2 focus:ring-[#7E5BEF]"
                      placeholder="Enter email subject"
                    />
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleSendEmail(
                      modal.type === "mentorEmail"
                        ? API_ENDPOINTS.SEND_EMAIL_TO_MENTOR
                        : API_ENDPOINTS.SEND_BULK_EMAILS,
                      modal.type === "mentorEmail"
                        ? [modal.data?.instructorName?.id]
                        : modal.data?.registeredUsers
                    )
                  }
                  className="px-4 py-2 rounded bg-[#7E5BEF] text-white hover:bg-[#674dd0]"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Users Modal */}
      {modal.type === "users" && (
        <Modal isOpen onClose={closeModal} width="w-[400px] md:w-[600px]">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Registered Users
            </h2>
            {getAllUsers().length > 0 ? (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                {getAllUsers().map((user) => (
                  <div
                    key={user.userId}
                    className="flex items-start p-4 bg-white rounded-lg shadow-md hover:shadow-lg"
                  >
                    {user.profilePhoto ? (
                      <img
                        src={user.profilePhoto}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-16 h-16 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                        <User className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {user.firstName} {user.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{user.currentJob}</p>
                      <p className="text-sm text-gray-500">
                        {[user.city, user.country].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        <span className="font-medium">Skills:</span>{" "}
                        {user.commonSkills?.join(", ") || "None"}
                      </p>
                      <button
                        onClick={() =>
                          navigate(`/dashboard/users/${user.userId}`)
                        }
                        className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        More Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No users found.</p>
            )}
          </div>
        </Modal>
      )}

      {/* Main Content */}
      <div className="flex flex-col gap-6">
        <WelcomeCard title="Course List" />
        {courses?.length ? (
          <div className="p-4 sm:p-6 bg-white rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Courses List
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="border-y bg-gray-100 text-gray-600 text-xs sm:text-sm uppercase">
                  <tr>
                    <th className="py-3 px-4">Course Name</th>
                    <th className="py-3 px-4">Instructor</th>
                    <th className="py-3 px-4">Seats</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-4 font-medium text-gray-800">
                        {course.courseName}
                      </td>
                      <td className="py-4 px-4 text-gray-800">
                        {course.instructorName}
                      </td>
                      <td className="py-4 px-4 text-gray-600">{course.seats}</td>
                      <td className="py-4 px-4 text-gray-600 truncate max-w-xs">
                        {course.courseDescription}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex justify-center flex-wrap rounded-md overflow-hidden">
                          <button
                            onClick={() =>
                              navigate(`/dashboard/courses/create/${course._id}`, {
                                state: { courseData: course },
                              })
                            }
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setModal({ type: "delete", data: course })}
                            className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-2"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setModal({ type: "mentorEmail", data: course })}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2"
                            title="Mentor Email"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setModal({ type: "menteesEmail", data: course })}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2"
                            title="Mentees Email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setModal({ type: "users" })}
                            className="bg-sky-600 hover:bg-sky-700 text-white px-3 py-2"
                            title="View Mentees"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center bg-gray-50 rounded-lg shadow-inner border">
            <GraduationCap className="w-12 h-12 text-indigo-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">
              No Courses Found
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              You haven’t created any courses yet.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ListCourse;
